import type {CSSProperties} from 'react';
import {WEDGE_JOURNAL} from '@territory/tokens';
import {font, leading, radius} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeJournalAreaVariant = 'wedge-journal-area' | 'wedge-journal-area-mobile';

export const isWedgeJournalAreaVariant = (variant: unknown): variant is WedgeJournalAreaVariant =>
  variant === 'wedge-journal-area' || variant === 'wedge-journal-area-mobile';

const PHONE_MIN_HEIGHT = 96, DESK_MIN_HEIGHT = 84, RING = 4, EASE_MS = 150;

/* Released composer textarea of the journal (the Notes card in the brief, the saved row note and the Notes app): one bare native textarea at
   full width on the surface fill, with a 1px border, a 12px radius, 10px by 12px padding, the Wedge font at line height 1.45, no resize and
   no scrollbar until the composer grows past its cap. The desktop look is 84px tall and 15px type; the phone look is 96px and 16px. The
   composer owns its focus state, so the caller says whether the field has focus and whether the theme is light: focused, the border takes
   the accent and a 4px ring of the accent tint replaces the shadow; at rest, the light themes cast the hairline journal shadow
   (WEDGE_JOURNAL.readShadow) and the dark theme casts none. Border color and shadow ease over 150ms on the legacy ease. The source sets
   `outline: none`, so it is kept as written; the shipped sheet draws the visible focus ring (:focus-visible always wins over the inline
   outline). The caller owns the value, the rows (2 on desktop, 3 on the phone), the handlers, the height that the composer grows to and
   the overflow it sets while it grows. Each color reads the exact legacy role, so the shipped ink rules keep matching. */
export function wedgeJournalAreaStyle(variant: WedgeJournalAreaVariant, focused = false, light = false): CSSProperties {
  const r = wedgeExactRole, phone = variant === 'wedge-journal-area-mobile';
  return {
    width: '100%', boxSizing: 'border-box', resize: 'none', overflow: 'hidden', padding: '10px 12px', borderRadius: radius.lg,
    minHeight: phone ? PHONE_MIN_HEIGHT : DESK_MIN_HEIGHT, background: r('surface'), color: r('label'),
    border: `1px solid ${focused ? r('accent') : r('cardBorder')}`, outline: 'none',
    boxShadow: focused ? `0 0 0 ${RING}px ${r('accentTint')}` : light ? WEDGE_JOURNAL.readShadow : 'none',
    fontFamily: r('font'), fontSize: phone ? font.headline : font.callout, lineHeight: leading.body,
    transition: `border-color ${EASE_MS}ms ${r('ease')}, box-shadow ${EASE_MS}ms ${r('ease')}`,
  };
}
